import { useState, useEffect, useCallback, useMemo, memo } from 'react'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import VehicleDetail from './VehicleDetailModal'
import VehicleListModal from './VehicleListModal'
import GenericAddButton from '../components/GenericAddButton'
import VehicleAddModal from './VehicleAddModal'
import VehicleDeleteModal from './VehicleDeleteModal'
import TableSortLabel from '@mui/material/TableSortLabel'
import RefreshButton from '../components/RefreshButton'
import ResetTableButton from '../components/ResetTableButton'
import VehicleLimitModal from './VehicleLimitModal'

import deleteIcon from '../assets/images/icons/delete.png'
import detailIcon from '../assets/images/icons/detail.png'
import DetailButton from '../components/DetailButtonModal'

// eslint-disable-next-line no-unused-vars
import GenericListFilter from './GenericListFilter'
// eslint-disable-next-line no-unused-vars
import FilterToggleButton from '../components/FilterToggleButton'
import GenericSearchToggle from '../components/GenericSearchToggle'
import useSharedModalUiState from '../hooks/useSharedModalUiState'
import useGenericTextSearch from '../hooks/useGenericTextSearch'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'

import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'

import { CSS } from '@dnd-kit/utilities'

const vehicleDetailFields = [
  ['name', 'vehicle_name'],
  ['licensePlate', 'vehicle_license_plate'],
  ['usageType', 'vehicle_usage_type'],
  ['vin', 'vehicle_vin'],
  ['brand', 'vehicle_brand'],
  ['model', 'vehicle_model'],
  ['variantType', 'vehicle_type'],
  ['vehicleType', 'vehicle_class'],
  ['category', 'vehicle_category'],
  ['fuelType', 'vehicle_fuel_type'],
  ['engineCapacity', 'vehicle_engine_capacity'],
  ['power', 'vehicle_power'],
  ['transmission', 'vehicle_transmission'],
  ['color', 'vehicle_color'],
  ['emissionsCo2', 'vehicle_emissions_co2'],
  ['consumption', 'vehicle_consumption'],
  ['emissionClass', 'vehicle_emission_class'],
  ['year', 'vehicle_year'],
  ['stkDate', 'vehicle_stk_date'],
  ['ekDate', 'vehicle_ek_date'],
  ['serviceDate', 'vehicle_service_date'],
  ['tiresAllSeason', 'vehicle_tires_all_season'],
  ['startDate', 'vehicle_start_date'],
  ['pzpDate', 'vehicle_pzp_date'],
  ['pzpFrequency', 'vehicle_pzp_frequency'],
  ['cascoDate', 'vehicle_casco_date'],
  ['cascoFrequency', 'vehicle_casco_frequency'],
  ['registrationCountry', 'vehicle_registration_country'],
  ['note', 'vehicle_note'],
]

const DEFAULT_COLUMNS = [
  { key: 'name', labelKey: 'vehicle_name' },
  { key: 'licensePlate', labelKey: 'vehicle_license_plate' },
  { key: 'brand', labelKey: 'vehicle_brand' },
  { key: 'model', labelKey: 'vehicle_model' },
  { key: 'year', labelKey: 'vehicle_year' },
  { key: 'fuelType', labelKey: 'vehicle_fuel_type' },
  { key: 'engineCapacity', labelKey: 'vehicle_engine_capacity' },
  { key: 'vin', labelKey: 'vehicle_vin' }
]

function SortableHeaderCell({
  col,
  orderBy,
  order,
  handleSort,
  colWidths,
  handleMouseDown,
  t
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: col.key })

  const customWidth = colWidths[col.key]

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(customWidth
      ? {
          width: customWidth,
          minWidth: customWidth,
          maxWidth: customWidth
        }
      : {
          flex: '1 1 0',
          minWidth: 0
        }),
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    paddingRight: 8,
    boxSizing: 'border-box'
  }

  return (
    <div ref={setNodeRef} style={style}>
      <span
        {...attributes}
        {...listeners}
        style={{
          cursor: 'grab',
          marginRight: 6,
          color: '#999'
        }}
      >
        ⠿
      </span>

      <TableSortLabel
        active={orderBy === col.key}
        direction={orderBy === col.key ? order : 'asc'}
        onClick={() => handleSort(col.key)}
      >
        {t(col.labelKey)}
      </TableSortLabel>

      {/* jemná zvislá čiara */}
	<div
	  style={{
 	   position: 'absolute',
	    right: 4,
 	   top: '20%',
  	  bottom: '20%',
 	   width: 1,
  	  background: '#9e9e9e'
 	 }}
	/>

      {/* resize zóna */}
      <div
        onMouseDown={(e) => handleMouseDown(e, col.key)}
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 6,
          cursor: 'col-resize'
        }}
      />
    </div>
  )
}

const VehicleRow = memo(function VehicleRow({
  vehicle,
  columns,
  colWidths,
  rowFontSize,
  isActive,
  isHovered,
  detailLabel,
  deleteLabel,
  detailColWidth,
  onOpenVehicle,
  onHoverVehicle,
  onClearHover,
  onDeleteVehicle
}) {
  return (
    <div
      onClick={() => onOpenVehicle(vehicle, true)}
      onMouseEnter={(e) => {
        if (e.target.closest('.vehicle-detail-button')) return
        onHoverVehicle(vehicle)
      }}
      onMouseMove={(e) => {
        if (e.target.closest('.vehicle-detail-button')) {
          onClearHover()
        } else {
          onHoverVehicle(vehicle)
        }
      }}
      onMouseLeave={(e) => {
        const related = e.relatedTarget
        if (related && related.closest && related.closest('.vehicle-tooltip')) return
        onClearHover()
      }}
      style={{
        display: 'flex',
        padding: '6px 2px 6px 12px',
        borderTop: '1px solid #ececec',
        alignItems: 'center',
        backgroundColor:
          isActive ? '#e6f4f1' :
          isHovered ? '#f3fafa' : '#fff',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        fontSize: rowFontSize
      }}
    >
      {columns.map(col => (
        <div
          key={col.key}
          style={{
            ...(colWidths[col.key]
              ? {
                  width: colWidths[col.key],
                  minWidth: colWidths[col.key],
                  maxWidth: colWidths[col.key]
                }
              : {
                  flex: '1 1 0',
                  minWidth: 0
                }),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {vehicle[col.key] || '-'}
        </div>
      ))}

      <div style={{
        flex: `0 0 ${detailColWidth}px`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
  marginRight: -5
      }}>
<DetailButton
  onClick={(e) => {
    e.stopPropagation()
    onOpenVehicle(vehicle, false)
  }}
/>
      </div>
<div style={{
  flex: `0 0 ${detailColWidth}px`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}}>
<button
  style={{
    border: '1.5px solid #a33',
    background: '#f5f5f5',
    borderRadius: 6,
    height: 28,
    width: 34,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 8px rgba(0,0,0,0.30)',
    transition: 'transform 0.05s ease, box-shadow 0.1s ease',
    outline: 'none'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.background = '#f3bcbc'
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = '#f5f5f5'
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
  }}
  onMouseDown={(e) => {
    e.currentTarget.style.transform = 'scale(0.96)'
    e.currentTarget.style.boxShadow = '0 0px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
    e.currentTarget.blur()
  }}
  onClick={(e) => {
    e.stopPropagation()
    onDeleteVehicle(vehicle)
  }}
>
  <img
    src={deleteIcon}
    alt="delete"
    style={{
      width: 16,
      height: 16,
      pointerEvents: 'none'
    }}
  />
</button>
</div>
    </div>
  )
})

const VehicleBox = ({ user, t, layout, viewport }) => {
  const [vehicles, setVehicles] = useState([])
  const [vehiclesLoading, setVehiclesLoading] = useState(true)
  const [vehiclesError, setVehiclesError] = useState(null)
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [vehicleDetailOpen, setVehicleDetailOpen] = useState(false)
  const [vehicleListFullscreen, setVehicleListFullscreen] = useState(false)
  const [hoveredVehicle, setHoveredVehicle] = useState(null)
  const [activeRow, setActiveRow] = useState(null)
const [tooltipTimer, setTooltipTimer] = useState(null)
  const [openExportDropdown, setOpenExportDropdown] = useState(false)
  const [addVehicleOpen, setAddVehicleOpen] = useState(false)
  const [vehicleLimitModalOpen, setVehicleLimitModalOpen] = useState(false)
  const [maxVehiclesLimit, setMaxVehiclesLimit] = useState(null)
  const [deleteVehicle, setDeleteVehicle] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

const { showSearch, setShowSearch } = useSharedModalUiState()
const [searchQuery, setSearchQuery] = useState('')

// eslint-disable-next-line no-unused-vars
const [showFilter, setShowFilter] = useState(() => {
  const stored = localStorage.getItem('vehicleFilterVisible')
  return stored === null ? false : stored === 'true'
})

const [orderBy, setOrderBy] = useState(() => {
  return localStorage.getItem('vehicleOrderBy') || 'name'
})

const [order, setOrder] = useState(() => {
  return localStorage.getItem('vehicleOrder') || 'asc'
})

const [columns, setColumns] = useState(() => {
  const stored = localStorage.getItem('vehicleColumns')
  if (stored) return JSON.parse(stored)

  return DEFAULT_COLUMNS
})

const [colWidths, setColWidths] = useState(() => {
  const stored = localStorage.getItem('vehicleColWidths')
  return stored ? JSON.parse(stored) : {}
})

const sensors = useSensors(useSensor(PointerSensor))
const handleSort = (col) => {
  if (orderBy === col) {
    setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
  } else {
    setOrderBy(col)
    setOrder('asc')
  }
}

const handleMouseDown = (e, key) => {
  const startX = e.clientX
  const startWidth = colWidths[key] || 150

  const onMouseMove = (e) => {
    const delta = e.clientX - startX
    const newWidth = Math.max(80, startWidth + delta)
    setColWidths(prev => ({ ...prev, [key]: newWidth }))
  }

  const onMouseUp = () => {
    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('mouseup', onMouseUp)
  }

  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp)
}

const handleDragEnd = (event) => {
  const { active, over } = event
  if (!over || active.id === over.id) return

  const oldIndex = columns.findIndex(c => c.key === active.id)
  const newIndex = columns.findIndex(c => c.key === over.id)

  const reordered = [...columns]
  const [moved] = reordered.splice(oldIndex, 1)
  reordered.splice(newIndex, 0, moved)

  setColumns(reordered)
}

const [tooltipEnabled, setTooltipEnabled] = useState(() => {
  const stored = localStorage.getItem('tooltipEnabled')
  return stored === null ? true : stored === 'true'
})

const [showFullscreenHint, setShowFullscreenHint] = useState(false)
const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 })

useEffect(() => {
  localStorage.setItem('tooltipEnabled', tooltipEnabled)
}, [tooltipEnabled])

useEffect(() => {
  localStorage.setItem('vehicleFilterVisible', showFilter)
}, [showFilter])

useEffect(() => {
  localStorage.setItem('vehicleColumns', JSON.stringify(columns))
}, [columns])

useEffect(() => {
  localStorage.setItem('vehicleColWidths', JSON.stringify(colWidths))
}, [colWidths])

useEffect(() => {
  localStorage.setItem('vehicleOrderBy', orderBy)
}, [orderBy])

useEffect(() => {
  localStorage.setItem('vehicleOrder', order)
}, [order])

  const loadVehicles = useCallback(async (opts = {}) => {
  const { silent = false } = opts
    if (!user?.id) return

    try {
      if (!silent) setVehiclesLoading(true)
      setVehiclesError(null)

      const res = await fetchWithAuth(`/users/${user.id}/vehicles`)
      if (!res.ok) {
        setVehiclesError('Nepodarilo sa načítať vozidlá.')
        return
      }

      const data = await res.json()
      setVehicles(Array.isArray(data) ? data : [])
    } catch {
      setVehiclesError('Chyba pripojenia pri načítaní vozidiel.')
    } finally {
      if (!silent) setVehiclesLoading(false)
    }
  }, [user?.id])

useEffect(() => {
  let cancelled = false

  const loadMaxVehiclesLimit = async () => {
    const membershipTypeId = Number(user?.membership_type_id)

    if (!Number.isFinite(membershipTypeId) || membershipTypeId <= 0) {
      setMaxVehiclesLimit(null)
      return
    }

    try {
      const res = await fetchWithAuth('/membership/types')
      if (!res.ok) {
        setMaxVehiclesLimit(null)
        return
      }

      const data = await res.json()
      const membershipTypes = Array.isArray(data) ? data : []
      const activeMembershipType = membershipTypes.find((item) => Number(item?.id) === membershipTypeId)
      const rawLimit = activeMembershipType?.max_vehicles
      const parsedLimit =
        rawLimit === null || rawLimit === undefined || rawLimit === ''
          ? null
          : Number(rawLimit)

      if (!cancelled) {
        setMaxVehiclesLimit(Number.isFinite(parsedLimit) && parsedLimit >= 0 ? parsedLimit : null)
      }
    } catch {
      if (!cancelled) setMaxVehiclesLimit(null)
    }
  }

  loadMaxVehiclesLimit()

  return () => {
    cancelled = true
  }
}, [user?.membership_type_id])

const vehicleLimitReached =
  maxVehiclesLimit !== null && vehicles.length >= maxVehiclesLimit

const handleOpenAddVehicle = useCallback(() => {
  if (vehicleLimitReached) {
    setVehicleLimitModalOpen(true)
    return
  }

  setAddVehicleOpen(true)
}, [vehicleLimitReached])

const filteredVehicles = useMemo(() => vehicles, [vehicles])

// eslint-disable-next-line no-unused-vars
const vehicleOptions = useMemo(() => {
  return vehicles.map((vehicle) => {
    const name = vehicle?.name || vehicle?.model || vehicle?.licensePlate || vehicle?.spz || `Vozidlo #${vehicle?.id}`
    const plate = vehicle?.licensePlate || vehicle?.spz || ''

    return {
      value: String(vehicle?.id),
      label: plate && name !== plate ? `${name} (${plate})` : name
    }
  })
}, [vehicles])

const getVehicleSearchData = useCallback((vehicle) => ({
  ...vehicle,
  vehicleName: vehicle?.name,
  vehicleModel: vehicle?.model,
  vehicleLicensePlate: vehicle?.licensePlate,
  vehicleSpz: vehicle?.spz
}), [])

const searchFilteredVehicles = useGenericTextSearch(
  filteredVehicles,
  searchQuery,
  getVehicleSearchData
)

  useEffect(() => {
    loadVehicles()
  }, [loadVehicles])

const handleVehicleSaved = useCallback((opts = {}) => {
  const delayMs = Number(opts?.delayMs || 0)

  if (delayMs > 0) {
    setTimeout(() => {
      loadVehicles()
    }, delayMs)
    return
  }

  loadVehicles()
}, [loadVehicles])

const sortedVehicles = [...searchFilteredVehicles].sort((a, b) => {
  let aVal = a[orderBy]
  let bVal = b[orderBy]

  if (orderBy === 'year' || orderBy === 'engineCapacity') {
    aVal = Number(aVal) || 0
    bVal = Number(bVal) || 0
  }

  if (typeof aVal === 'string') aVal = aVal.toLowerCase()
  if (typeof bVal === 'string') bVal = bVal.toLowerCase()

  if (aVal > bVal) return order === 'asc' ? 1 : -1
  if (aVal < bVal) return order === 'asc' ? -1 : 1
  return 0
})

  const detailColWidth = viewport <= 900 ? 110 : 50

const handleDeleteVehicleClick = useCallback((vehicle) => {
  setDeleteVehicle(vehicle)
  setDeleteModalOpen(true)
}, [])

const handleOpenVehicle = useCallback((vehicle, markRow = false) => {
  if (markRow) setActiveRow(vehicle.id)
  setSelectedVehicle(vehicle)
  setVehicleDetailOpen(true)
}, [])

const selectedVehicleIndex = sortedVehicles.findIndex(
  (item) => item?.id === selectedVehicle?.id
)

const handlePrevVehicle = useCallback(() => {
  if (selectedVehicleIndex <= 0) return
  const prevVehicle = sortedVehicles[selectedVehicleIndex - 1]
  setSelectedVehicle(prevVehicle)
  setActiveRow(prevVehicle?.id ?? null)
}, [selectedVehicleIndex, sortedVehicles])

const handleNextVehicle = useCallback(() => {
  if (selectedVehicleIndex < 0 || selectedVehicleIndex >= sortedVehicles.length - 1) return
  const nextVehicle = sortedVehicles[selectedVehicleIndex + 1]
  setSelectedVehicle(nextVehicle)
  setActiveRow(nextVehicle?.id ?? null)
}, [selectedVehicleIndex, sortedVehicles])

const handleHoverVehicle = useCallback((vehicle) => {
  setHoveredVehicle(prev => {
    if (prev?.id === vehicle?.id) return prev
    return vehicle
  })
}, [])

const handleClearHoveredVehicle = useCallback(() => {
  setHoveredVehicle(prev => (prev == null ? prev : null))
}, [])

// eslint-disable-next-line no-unused-vars
const filterLabels = useMemo(() => ({
  allVehicles: t('TRIPLOG.BUTTON.ALL_VEHICLES'),
  from: t('TRIPLOG.FILTER_FROM'),
  to: t('TRIPLOG.FILTER_TO'),
  month: t('TRIPLOG.FILTER_MONTH'),
  year: t('TRIPLOG.FILTER_YEAR'),
  clear: t('TRIPLOG.BUTTON.CLEAR')
}), [t])

  return (
    <div
      style={{
        background: '#ededed',
        padding: 12,
        border: '1.5px solid #107c6b',
        borderRadius: 10,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}
      >

<GenericAddButton
  onToggle={handleOpenAddVehicle}
/>

<GenericSearchToggle
  visible={showSearch}
  query={searchQuery}
  onQueryChange={setSearchQuery}
  onToggle={() => {
    setShowSearch(prev => {
      if (prev) setSearchQuery('')
      return !prev
    })
  }}
  placeholder={t('TRIPLOG.SEARCH_PLACEHOLDER', { defaultValue: 'Hľadať...' })}
/>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
            fontSize: 13,
            userSelect: 'none',
    	    marginTop: 5
          }}
        >
	<input
	  type="checkbox"
	  checked={tooltipEnabled}
	  onChange={(e) => setTooltipEnabled(e.target.checked)}
	  style={{
	    cursor: 'pointer',
	    accentColor: '#06857a',
 	   appearance: 'none',
  	  width: 16,
  	  height: 16,
  	  border: '1px solid #1e3f44',
  	  borderRadius: 3,
	    background: tooltipEnabled ? '#06857a' : '#fff'
	  }}
	/>
          {t('vehicle_preview_toggle')}
        </label>

<RefreshButton onClick={() => loadVehicles({ silent: true })} />

<ResetTableButton
  isModified={
    Object.keys(colWidths).length > 0 ||
    JSON.stringify(columns.map(c => c.key)) !== JSON.stringify(DEFAULT_COLUMNS.map(c => c.key)) ||
    orderBy !== 'name' ||
    order !== 'asc'
  }
  onReset={() => {
const resetCols = DEFAULT_COLUMNS

    setColumns(resetCols)
    setColWidths({})
    setOrderBy('name')
    setOrder('asc')

    localStorage.removeItem('vehicleColumns')
    localStorage.removeItem('vehicleColWidths')
    localStorage.removeItem('vehicleOrderBy')
    localStorage.removeItem('vehicleOrder')
  }}
/>

<button
  onMouseEnter={(e)=>{
    e.currentTarget.style.background='#2da9a4'
  }}
  onMouseLeave={(e)=>{
    e.currentTarget.style.background='#f5f5f5'
    e.currentTarget.style.transform='scale(1)'
    e.currentTarget.style.boxShadow='0 1px 8px rgba(0,0,0,0.30)'
  }}
  onMouseDown={(e)=>{
    e.currentTarget.style.transform='scale(0.96)'
    e.currentTarget.style.boxShadow='0 0px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e)=>{
    e.currentTarget.style.transform='scale(1)'
    e.currentTarget.style.boxShadow='0 1px 8px rgba(0,0,0,0.30)'
  }}
  onClick={(e)=>{
    e.currentTarget.blur()
    setOpenExportDropdown(true)
    setVehicleListFullscreen(true)
  }}
  style={{
    background:'#f5f5f5',
    border:'1px solid #107c6b',
    borderRadius:6,
    height:24,
    padding:'0 10px',
    cursor:'pointer',
    fontWeight:700,
    boxShadow:'0 1px 8px rgba(0,0,0,0.30)',
    transition:'transform 0.05s ease, box-shadow 0.1s ease',
    marginTop:5
  }}
>
  {t('TRIPLOG.BUTTON_EXPORT')}
</button>

<button
  onMouseEnter={(e) => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(true)
    }, 600)

    setTooltipTimer(timer)
  }}
  onMouseLeave={(e) => {
    if (tooltipTimer) clearTimeout(tooltipTimer)

    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  onMouseDown={(e) => {
    e.currentTarget.style.transform = 'scale(0.96)'
    e.currentTarget.style.boxShadow = '0 0px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = 'none'
  }}
  onClick={(e) => {
    if (tooltipTimer) clearTimeout(tooltipTimer)

    e.currentTarget.blur()
    setVehicleListFullscreen(true)
  }}
  style={{
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 32
  }}
>
  ⛶
</button>
</div>
      <h3 style={{ fontSize: 18, marginTop: 0, marginBottom: 10 }}>
        {t('vehicle_list_title')}
      </h3>

      {vehiclesLoading && vehicles.length === 0 && <div>Načítavam...</div>}
      {vehiclesError && <div style={{ color: 'red' }}>{vehiclesError}</div>}

<div
  style={{
    border: '1px solid #c7c7c7',
    borderRadius: 10,
    background: '#fff',
    flex: 1,
    minHeight: 0,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  }}
>
<DndContext
  sensors={sensors}
  collisionDetection={closestCenter}
  onDragEnd={handleDragEnd}
>
  <SortableContext
    items={columns.map(c => c.key)}
    strategy={horizontalListSortingStrategy}
  >
<div
  style={{
    display: 'flex',
    background: '#c8e1e1',
    padding: '8px 12px',
    fontWeight: 700,
    fontSize: layout.vehicleHeaderFontSize
  }}
>
      {columns.map(col => (
        <SortableHeaderCell
          key={col.key}
          col={col}
          orderBy={orderBy}
          order={order}
          handleSort={handleSort}
          colWidths={colWidths}
          handleMouseDown={handleMouseDown}
 	  t={t}
        />
      ))}

<div
  onMouseEnter={(e) => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(t('TRIPLOG.VEHICLES.DETAIL'))
    }, 600)
    setTooltipTimer(timer)
  }}
  onMouseLeave={() => {
    if (tooltipTimer) clearTimeout(tooltipTimer)
    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
>
  <img
    src={detailIcon}
    alt="detail"
    style={{ width: 22, height: 22 }}
  />
</div>
<div
  onMouseEnter={(e) => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(t('TRIPLOG.VEHICLES.DELETE_BUTTON'))
    }, 600)
    setTooltipTimer(timer)
  }}
  onMouseLeave={() => {
    if (tooltipTimer) clearTimeout(tooltipTimer)
    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  marginRight: -6,
  marginLeft: -6
  }}
>
  <img
    src={deleteIcon}
    alt="delete"
    style={{
      width: 22,
      height: 22
    }}
  />
</div>
    </div>
  </SortableContext>
</DndContext>
<div
  style={{
    overflow: 'auto',
    flex: 1,
    minHeight: 0
  }}
>
{sortedVehicles.map(vehicle => (
  <VehicleRow
    key={vehicle.id}
    vehicle={vehicle}
    columns={columns}
    colWidths={colWidths}
    rowFontSize={layout.vehicleRowFontSize}
    isActive={activeRow === vehicle.id}
    isHovered={hoveredVehicle?.id === vehicle.id}
    detailLabel={t('vehicle_detail_title')}
    detailColWidth={detailColWidth}
    onOpenVehicle={handleOpenVehicle}
    onHoverVehicle={handleHoverVehicle}
    onClearHover={handleClearHoveredVehicle}
deleteLabel={t('TRIPLOG.BUTTON.DELETE')}
onDeleteVehicle={handleDeleteVehicleClick}
  />
))}
</div>
      </div>

      {tooltipEnabled && hoveredVehicle && (
        <div
          className="vehicle-tooltip"
          onMouseLeave={() => setHoveredVehicle(null)}
              style={{
                position: 'fixed',
                right:
                  viewport >= 1920 ? 9 :
                  viewport >= 1600 ? 9 :
                  viewport >= 1366 ? 10 : 20,
                bottom:
                  viewport >= 1920 ? 50 :
                  viewport >= 1600 ? 50 :
                  viewport >= 1366 ? 49 : 30,
                fontSize:
                  viewport >= 1920 ? 14 :
                  viewport >= 1600 ? 14 :
                  viewport >= 1366 ? 13 : 13,
                maxHeight:
                  viewport >= 1920 ? '80vh' :
                  viewport >= 1600 ? '75vh' : '96vh',
                background: '#f3fafa',
                border: '1px solid #1e3f44',
                borderRadius: 8,
                padding: '16px 20px',
                boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
                zIndex: 9999,
                overflowY: 'auto'
              }}
            >
          {vehicleDetailFields.map(([key, labelKey]) => (
            <div key={key} style={{ marginBottom: 4, display: 'flex' }}>
              <strong style={{ minWidth: 300 }}>
                {t(labelKey)}:
              </strong>
<span style={{ color: '#1e3f44', fontWeight: 600 }}>
{['stkDate','ekDate','serviceDate','startDate','pzpDate','cascoDate'].includes(key)
  ? (hoveredVehicle[key]
      ? new Date(hoveredVehicle[key]).toLocaleDateString('sk-SK')
      : '-')
  : (key === 'pzpFrequency' || key === 'cascoFrequency')
    ? (() => {
        const freq = Number(hoveredVehicle[key])
        if (freq === 1) return t('vehicle_payment_yearly')
        if (freq === 2) return t('vehicle_payment_halfyear')
        if (freq === 4) return t('vehicle_payment_quarterly')
        return hoveredVehicle[key] || '-'
      })()
: key === 'usageType'
  ? (hoveredVehicle[key] === 'firemne'
      ? t('vehicle_usage_type_company')
      : hoveredVehicle[key] === 'sukromne'
        ? t('vehicle_usage_type_private')
        : '-')
: key === 'tiresAllSeason' || key === 'hasCasco'
  ? (hoveredVehicle[key] ? t('dropdown_yes') : t('dropdown_no'))
  : hoveredVehicle[key] || '-'}
</span>
            </div>
          ))}
        </div>
      )}

{showFullscreenHint && (
  <div
          style={{
            position: 'fixed',
    	    top: cursorPos.y - 50,
            left: cursorPos.x,
     	    transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.85)',
            color: '#E7DFEC',
            padding: '6px 14px 8px 14px',
            borderRadius: 12,
            fontSize: 16,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 5000,
            border: '1.5px solid #1e3f44',
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1
          }}
  >
    {typeof showFullscreenHint === 'string'
  ? showFullscreenHint
  : t('MODAL_DETAILS')}
  </div>
)}

<VehicleListModal
  open={vehicleListFullscreen}
  openExportDropdown={openExportDropdown}
  onClose={() => {
    setVehicleListFullscreen(false)
    setOpenExportDropdown(false)
  }}
  vehicles={vehicles}
  vehiclesLoading={vehiclesLoading}
  vehiclesError={vehiclesError}
  onVehicleSaved={handleVehicleSaved}
/>

<VehicleLimitModal
  open={vehicleLimitModalOpen}
  onClose={() => setVehicleLimitModalOpen(false)}
  t={t}
  maxVehiclesLimit={maxVehiclesLimit}
/>

<VehicleAddModal
  open={addVehicleOpen}
  onClose={() => setAddVehicleOpen(false)}
  onSaved={(opts) => {
    setAddVehicleOpen(false)
    handleVehicleSaved(opts)
  }}
/>

      <VehicleDetail
        open={vehicleDetailOpen}
        onClose={() => {
          setVehicleDetailOpen(false)
          setSelectedVehicle(null)
          setActiveRow(null)
        }}
        vehicle={selectedVehicle}
        onSaved={handleVehicleSaved}
        onPrevVehicle={handlePrevVehicle}
        onNextVehicle={handleNextVehicle}
        hasPrevVehicle={selectedVehicleIndex > 0}
        hasNextVehicle={selectedVehicleIndex >= 0 && selectedVehicleIndex < sortedVehicles.length - 1}
      />

<VehicleDeleteModal
  open={deleteModalOpen}
  vehicle={deleteVehicle}
  onClose={() => {
    setDeleteModalOpen(false)
    setDeleteVehicle(null)
  }}
  onDeleted={() => {
    setDeleteModalOpen(false)
    setDeleteVehicle(null)
    loadVehicles()
  }}
  t={t}
/>
    </div>
  )
}

export default VehicleBox